import type { ImgHTMLAttributes } from 'react'
import { cn } from '../../lib/cn'
import add01 from './svg/add-01.svg'
import arrowDown from './svg/arrow-down.svg'
import arrowLeft01 from './svg/arrow-left-01.svg'
import arrowUp from './svg/arrow-up.svg'
import attachment01 from './svg/attachment-01.svg'
import calendar02 from './svg/calendar-02.svg'
import calendar03 from './svg/calendar-03.svg'
import chartHistogram from './svg/chart-histogram.svg'
import chevronDown from './svg/chevron-down.svg'
import chevronDownSm from './svg/chevron-down-sm.svg'
import close from './svg/close.svg'
import cloudUpload from './svg/cloud-upload.svg'
import copy01 from './svg/copy-01.svg'
import delete02 from './svg/delete-02.svg'
import download01 from './svg/download-01.svg'
import download01Light from './svg/download-01-light.svg'
import image from './svg/image.svg'
import image01 from './svg/image-01.svg'
import logout05 from './svg/logout-05.svg'
import notification from './svg/notification.svg'
import notificationUnread from './svg/notification-unread.svg'
import open from './svg/open.svg'
import pdf from './svg/pdf.svg'
import relieved01 from './svg/relieved-01.svg'
import search from './svg/search.svg'
import search01 from './svg/search-01.svg'
import share04 from './svg/share-04.svg'
import tick02 from './svg/tick-02.svg'
import toastSuccess from './svg/toast-success.svg'
import toastWarning from './svg/toast-warning.svg'
import user from './svg/user.svg'
import x from './svg/x.svg'

// Every SVG is the Figma export of one icon, untouched: the colour and size are
// baked in, so a glyph the Figma draws in two colours is two entries (download-01
// in Accent, download-01-light in white on the primary button).
const ICONS = {
	'add-01': add01,
	'arrow-down': arrowDown,
	'arrow-left-01': arrowLeft01,
	'arrow-up': arrowUp,
	'attachment-01': attachment01,
	'calendar-02': calendar02,
	'calendar-03': calendar03,
	'chart-histogram': chartHistogram,
	'chevron-down': chevronDown,
	'chevron-down-sm': chevronDownSm,
	close,
	'cloud-upload': cloudUpload,
	'copy-01': copy01,
	'delete-02': delete02,
	'download-01': download01,
	'download-01-light': download01Light,
	image,
	'image-01': image01,
	'logout-05': logout05,
	notification,
	'notification-unread': notificationUnread,
	open,
	pdf,
	'relieved-01': relieved01,
	search,
	'search-01': search01,
	'share-04': share04,
	'tick-02': tick02,
	'toast-success': toastSuccess,
	'toast-warning': toastWarning,
	user,
	x,
} as const

/** The name of an icon exported from the UI Kit Figma, as named in its layers. */
export type IconName = keyof typeof ICONS

/** Every available icon name, for pickers and stories. */
export const ICON_NAMES = Object.keys(ICONS) as IconName[]

export interface IconProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'> {
	name: IconName
	/** Accessible name. Omit it for a decorative icon, which is then hidden from assistive technology. */
	label?: string
}

/**
 * An icon from the UI Kit Figma (Icons board and the icons inside the Components
 * board), rendered as the exported SVG at its own size and colour. Colour is not a
 * prop: the Figma ships each colour as its own asset, so pick the name that
 * matches the surface.
 */
export function Icon({ name, label, className, ...props }: IconProps) {
	return (
		<img
			{...props}
			src={ICONS[name]}
			alt={label ?? ''}
			aria-hidden={label ? undefined : true}
			data-icon={name}
			className={cn('block shrink-0', className)}
		/>
	)
}
